import { Box, Button, Flex, HStack, VStack } from "@chakra-ui/react";
import useAssets from "hooks/useAssets";
import useTranslate from "hooks/useTranslate";
import useHoneyInJars from "hooks/useHoneyInJars";
import { BigNumber, constants } from "ethers";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formatEther } from "ethers/lib/utils";
import useConnectedAccount from "hooks/useConnectedAccount";
import { getShortenedAddress } from "utils/walletUtils";
import HoneyPurchaseContainerSlider from "components/PurchaseSlider";
import TinyHeading from "components/TinyHeading";
import ScrollableArea from "components/ScrollableArea";
import FancyBearSelect from "components/FancyBearSelect";
import { formatEtherBalance, generateRandomInteger } from "utils/numberUtils";
import useTraitSales from "hooks/useTraitSales";
import usePurchaseTraitsUsingHoney from "hooks/usePurchaseTraitsUsingHoney";
import useHoneyTraitsSaleAllowance from "hooks/useHoneyTraitsSaleAllowance";
import useApproveHoney from "hooks/useApproveHoney";
import { fancyBearsSupply, traitSaleContract } from "config";
import FancyBearImage from "components/FancyBearImage";

export type HoneyPurchaseContainerValue = {
  amount: BigNumber;
  isLocked?: boolean;
};

export type HoneyPurchaseContainerValues = Record<
  string,
  HoneyPurchaseContainerValue
> & {
  wallet?: HoneyPurchaseContainerValue;
};

type HoneyPurchaseContainerProps = {
  tokenId: number;
  traitsToBuy: number[];
  onSuccess: () => void;
};

const updateValue = (
  valueKey: string,
  newValue: HoneyPurchaseContainerValue,
  originalValues: HoneyPurchaseContainerValues
) => ({
  ...originalValues,
  [valueKey]: {
    ...(originalValues[valueKey] || {
      isLocked: false,
      amount: BigNumber.from("0"),
    }),
    ...newValue,
  },
});

const distributeValues = (
  currentValues: HoneyPurchaseContainerValues,
  totalPrice: BigNumber,
  valueKey?: string,
  valueAmount?: BigNumber
) => {
  const currentAmount = valueAmount ? valueAmount : BigNumber.from("0");
  const valuesToUpdate = Object.entries(currentValues).filter(
    ([entryKey]) => entryKey !== valueKey
  );

  const otherValuesAmount = valuesToUpdate
    .reduce(
      (sum: BigNumber, [, { amount }]) => sum.add(amount),
      BigNumber.from("0")
    )
    .add(currentAmount);
  const unlockedValues = valuesToUpdate.filter(([, { isLocked }]) => !isLocked);

  let values: HoneyPurchaseContainerValues = { ...currentValues };
  let amountToDistribute = totalPrice.sub(otherValuesAmount);

  if (amountToDistribute.lt(0)) {
    while (amountToDistribute.lt(0) && unlockedValues.length > 0) {
      const [entryKey, entryValue] = unlockedValues.pop()!;
      const bnAmount = BigNumber.from(entryValue.amount);
      const distributedAmount = bnAmount.gt(amountToDistribute.abs())
        ? amountToDistribute.abs()
        : bnAmount;

      amountToDistribute = amountToDistribute.add(distributedAmount);

      values = updateValue(
        entryKey,
        {
          amount: bnAmount.sub(distributedAmount),
        },
        values
      );
    }
  }

  if (valueKey) {
    values = updateValue(
      valueKey,
      {
        amount: amountToDistribute.lt(0)
          ? currentAmount.add(amountToDistribute)
          : currentAmount,
      },
      values
    );
  }

  return values;
};

const formatPurchaseData = (
  values: HoneyPurchaseContainerValues,
  traitsToBuy: number[]
) => {
  if (values) {
    const honeyJars = Object.keys(values)
      .filter((key) => key.startsWith("jar_"))
      .map((tokenId) => ({
        tokenId: parseInt(tokenId.replace("jar_", ""), 10),
        amount: values[tokenId]?.amount || BigNumber.from("0"),
      }))
      .filter(({ amount }) => !BigNumber.from(amount).isZero());

    return {
      amountToSpendFromWallet: values.wallet?.amount || BigNumber.from("0"),
      honeyJars: honeyJars.map(({ tokenId }) => tokenId),
      amountToSpendFromHoneyJars: honeyJars.map(({ amount }) => amount),
      traitTokenIds: traitsToBuy,
      amountPerTrait: traitsToBuy.map(() => 1),
    };
  }
};

const HoneyPurchaseContainer = ({
  tokenId,
  traitsToBuy,
  onSuccess,
}: HoneyPurchaseContainerProps) => {
  const translate = useTranslate();
  const [address] = useConnectedAccount();
  const { traitSalesByTokenId } = useTraitSales();
  const { honeyBalance } = useAssets();
  const honeyInJars = useHoneyInJars();
  const [allowance, readAllowance] = useHoneyTraitsSaleAllowance(address);
  const [fancyBear, setFancyBear] = useState<number>();

  const totalPrice = useMemo(
    () =>
      traitsToBuy.reduce(
        (prev: BigNumber, tokenId) =>
          prev.add(traitSalesByTokenId?.[tokenId].price || "0"),
        BigNumber.from("0")
      ),
    [traitsToBuy, traitSalesByTokenId]
  );

  const [values, setValues] = useState<HoneyPurchaseContainerValues>({});

  const currentTotalValue = useMemo(
    () =>
      Object.values(values).reduce(
        (sum: BigNumber, curr) => sum.add(curr.amount),
        BigNumber.from("0")
      ),
    [values]
  );

  const isFree = totalPrice.eq("0");
  const isTotalAmount = currentTotalValue.eq(totalPrice);
  const isApproved = allowance?.gte(totalPrice) || false;

  const currentTotalPercentage = isFree
    ? 100
    : (parseInt(formatEther(currentTotalValue)) /
        parseInt(formatEther(totalPrice))) *
      100;

  const purchaseData = useMemo(
    () => formatPurchaseData(values, traitsToBuy),
    [values, traitsToBuy]
  );

  const [purchase, isPurchasing] = usePurchaseTraitsUsingHoney(
    purchaseData,
    onSuccess
  );

  const [approve, isApproving] = useApproveHoney(
    traitSaleContract.address,
    constants.MaxUint256,
    readAllowance
  );

  const jarsWithHoney = useMemo(
    () =>
      honeyInJars
        ? Object.entries(honeyInJars).filter(
            ([, honeyAmount]) => !honeyAmount.isZero()
          )
        : [],
    [honeyInJars]
  );

  const handleChange = useCallback(
    (valueKey: keyof HoneyPurchaseContainerValues, value: BigNumber) => {
      setValues(distributeValues(values, totalPrice, valueKey, value));
    },
    [values, totalPrice]
  );

  const handleLockToggle = useCallback(
    (key: keyof HoneyPurchaseContainerValues, isLocked?: boolean) => {
      setValues({
        ...values,
        [key]: {
          ...(values[key] || { amount: BigNumber.from("0") }),
          isLocked,
        },
      });
    },
    [values]
  );

  const handlePickRandomBear = useCallback(() => {
    setFancyBear(generateRandomInteger(1, fancyBearsSupply));
  }, []);

  useEffect(() => {
    if (!Object.keys(values).length && honeyBalance && honeyInJars && tokenId) {
      const initialValues = {
        ...Object.entries(honeyInJars).reduce(
          (prev, [tokenId, amount]) => ({
            ...prev,
            [`jar_${tokenId}`]: {
              amount,
              isLocked: false,
            },
          }),
          {}
        ),
        wallet: {
          amount: honeyBalance || BigNumber.from("0"),
          isLocked: false,
        },
      };

      setValues(distributeValues(initialValues, totalPrice));
    }
  }, [tokenId, values, totalPrice, honeyBalance, honeyInJars]);

  return (
    <>
      <Box mb="2" position="relative" zIndex="dropdown">
        <TinyHeading my="2" mt="2">
          {translate("purchase:fancyBear:title")}
        </TinyHeading>

        <HStack borderRadius="md" spacing="3" bg="dark.800" px="3" py="3">
          <FancyBearImage
            tokenId={fancyBear}
            objectFit="cover"
            transition="all 0.15s"
            size="256"
            w="10"
            flex="none"
          />

          <Box w="full">
            <FancyBearSelect
              tokenId={fancyBear}
              isClearable
              onChange={(tokenId) => setFancyBear(tokenId)}
            />
          </Box>

          <Button
            size="sm"
            colorScheme="dark"
            flex="none"
            onClick={handlePickRandomBear}
          >
            {translate("purchase:fancyBear:random")}
          </Button>
        </HStack>
      </Box>

      {!isFree && (
        <>
          {address && honeyBalance && !honeyBalance.isZero() && (
            <>
              <TinyHeading mb="2">
                {translate("purchase:wallet:title")}
              </TinyHeading>

              <HoneyPurchaseContainerSlider
                valueKey="wallet"
                values={values}
                max={honeyBalance}
                label={getShortenedAddress(address, 3)}
                onChange={handleChange}
                onLockToggle={handleLockToggle}
              />
            </>
          )}

          {jarsWithHoney.length > 0 && (
            <Flex
              direction="column"
              maxH="full"
              overflow="hidden"
              justify="stretch"
            >
              <TinyHeading my="2">
                {translate("purchase:honeyJars:title")}
              </TinyHeading>

              <ScrollableArea>
                <VStack spacing="2px">
                  {jarsWithHoney.map(([tokenId, honeyAmount]) => (
                    <HoneyPurchaseContainerSlider
                      key={tokenId}
                      valueKey={`jar_${tokenId}`}
                      values={values}
                      max={honeyAmount || BigNumber.from("0")}
                      label={`#${tokenId}`}
                      onChange={handleChange}
                      onLockToggle={handleLockToggle}
                    />
                  ))}
                </VStack>
              </ScrollableArea>
            </Flex>
          )}
        </>
      )}

      <Box
        position="relative"
        mt={isFree ? "4" : "6"}
        borderRadius="md"
        overflow="hidden"
      >
        {allowance === undefined ? (
          <Button w="full" colorScheme="dark" isLoading />
        ) : (
          <>
            {!isApproved ? (
              <Button
                w="full"
                onClick={() => approve()}
                isLoading={isApproving}
              >
                {translate("purchase:approve")}
              </Button>
            ) : (
              <>
                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  bottom="0"
                  width={`${currentTotalPercentage}%`}
                  bg="secondary.500"
                  transition="all 0.15s"
                />

                <Button
                  w="full"
                  colorScheme="secondary"
                  onClick={() => purchase()}
                  isDisabled={!isTotalAmount || !isApproved}
                  isLoading={isPurchasing}
                >
                  {translate(
                    isFree ? "purchase:submit:free" : "purchase:submit",
                    {
                      price: formatEtherBalance(totalPrice),
                      currency: "$HONEY",
                    }
                  )}
                </Button>
              </>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default HoneyPurchaseContainer;
